import type {
  JSONObject,
  JSONValue,
  Options,
  Traits,
  ID,
  Callback,
  DynamicJitsuOptions,
} from '@jitsu/protocols/analytics'
import { extendSession } from '@analytics/session-utils'
import { v4 as uuidv4 } from 'uuid'
import type { TrackingMode } from '@chilipiper/web-data-external'
import { refreshClientIds } from './clientIds'
import { logger } from './logger'
import { refreshMapIds } from './mapIds'
import { emitTrackingEvent } from './trackingEvents'

const SESSION_TIMEOUT_MINUTES = 30

let paused = false
let mode: TrackingMode = 'full'
let pendingQueue: Array<() => void> = []

export const pauseTracking = (): void => {
  paused = true
}

export const resumeTracking = (): void => {
  paused = false
}

/**
 * Which events leave the browser: everything in `full`, identify alone in `identify`, nothing in
 * `off`. Set from init, and checked here rather than at each call site so there is one place that
 * decides what goes out, whether the call came from one of the automatic listeners or from an
 * explicit call on the public API.
 */
export const setTrackingMode = (value: TrackingMode): void => {
  mode = value
}

const dropInMode = (method: 'track' | 'page' | 'identify' | 'group'): boolean => {
  if (mode === 'full') return false
  if (mode === 'identify' && method === 'identify') return false
  logger.log(`[jitsu] ${method} dropped in ${mode} mode`)
  return true
}

export const flushQueue = (): void => {
  const queue = pendingQueue
  pendingQueue = []
  queue.forEach(fn => fn())
}

const refreshSession = (): void => {
  if (!window.jitsu) return
  const session = extendSession(SESSION_TIMEOUT_MINUTES)
  window.jitsu.setContextProperty('sessionId', session.id)
}

export const jitsu = {
  track: (
    eventName: string | object,
    properties?: object | Callback,
    options?: Options | Callback,
    callback?: Callback
  ) => {
    if (dropInMode('track')) return
    if (paused) return
    if (!window.jitsu) {
      pendingQueue.push(() => jitsu.track(eventName, properties, options, callback))
      return
    }
    refreshSession()
    refreshClientIds()
    refreshMapIds()
    logger.log('[jitsu] track', eventName, properties)
    const result = window.jitsu.track(
      eventName as string | JSONObject,
      properties as JSONObject | Callback,
      options,
      callback
    )
    if (typeof eventName === 'string') {
      emitTrackingEvent(eventName, typeof properties === 'function' ? undefined : properties)
    }
    return result
  },

  page: (
    category?: string | object,
    name?: string | object | Callback,
    properties?: object | Options | Callback | null,
    options?: Options | Callback,
    callback?: Callback
  ) => {
    if (dropInMode('page')) return
    if (paused) return
    if (!window.jitsu) {
      pendingQueue.push(() => jitsu.page(category, name, properties, options, callback))
      return
    }
    refreshSession()
    refreshClientIds()
    refreshMapIds()
    window.jitsu.setContextProperty('pageViewId', uuidv4())
    logger.log('[jitsu] page', { url: window.location.href })
    const result = window.jitsu.page(category, name, properties, options, callback)
    emitTrackingEvent('page', {
      url: window.location.href,
      path: window.location.pathname,
      search: window.location.search,
      title: document.title,
    })
    return result
  },

  identify: (id?: ID | Traits, traits?: Traits | Callback | null, callback?: Callback) => {
    if (dropInMode('identify')) return
    if (paused) return
    if (!window.jitsu) {
      pendingQueue.push(() => jitsu.identify(id, traits, callback))
      return
    }
    refreshClientIds()
    refreshMapIds()
    logger.log('[jitsu] identify', id, traits)
    const result = window.jitsu.identify(id, traits, callback)
    emitTrackingEvent('identify', typeof traits === 'function' ? undefined : (traits ?? id))
    return result
  },

  group: (
    groupId?: ID | object,
    traits?: Traits | null,
    options?: Options,
    callback?: Callback
  ) => {
    if (dropInMode('group')) return
    if (paused) return
    if (!window.jitsu) {
      pendingQueue.push(() => jitsu.group(groupId, traits, options, callback))
      return
    }
    return window.jitsu.group(groupId, traits, options, callback)
  },

  reset: (callback?: (...params: unknown[]) => unknown) => {
    if (!window.jitsu) return
    return window.jitsu.reset(callback)
  },
  user: () => {
    if (!window.jitsu) return
    return window.jitsu.user()
  },
  setAnonymousId: (id: string | undefined) => {
    if (!window.jitsu) return
    window.jitsu.setAnonymousId(id)
  },
  setContextProperty: (name: string, value: JSONValue) => {
    if (!window.jitsu) return
    window.jitsu.setContextProperty(name, value)
  },
  getContextProperty: (name: string) => {
    if (!window.jitsu) return
    return window.jitsu.getContextProperty(name)
  },
  configure: (options: DynamicJitsuOptions) => {
    if (!window.jitsu) return
    window.jitsu.configure(options)
  },
  getConfiguration: () => {
    if (!window.jitsu) return
    return window.jitsu.getConfiguration()
  },
  isReady: () => !!window.jitsu,
}
